import { CSSProperties, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import { ProductCarouselSlide } from 'shared-types'
import { ProductVideo } from '~/components/ProductVideo'
import { convertProductImageUrl, getImageSrc } from '~/helpers'
import { usePage } from '~/hooks'
import styles from './Gallery.module.scss'
import {
  DEFAULT_SIZES,
  DEFAULT_SRCSET_WIDTHS,
  GalleryImage,
} from './GalleryImage'
import {
  GalleryThumbnails,
  getVisibleThumbnailSlides,
  hasVisibleThumbnails,
} from './GalleryThumbnails'
import { useImageAspectRatio } from './useImageAspectRatio'
import { usePreloadSlideImages } from './usePreloadSlideImages'
import { useSwipeNavigation } from './useSwipeNavigation'

export interface GalleryProps {
  slides: ProductCarouselSlide[]
  selectedIndex: number
  onSelect: (index: number) => void
  onImageClick?: () => void
  showNavButtons?: boolean
  zoomable?: boolean
  // Leave video slides out of the thumbnail strip and of prev / next paging,
  // so the gallery only ever moves between images.
  imagesOnly?: boolean
  // Forwarded to GalleryImage; see its props for the defaults.
  imageSrcSetWidths?: number[]
  imageSizes?: string
  // Warm the cache with the other image slides so paging is instant. Off by
  // default; the page gallery turns it on, and the modal uses the same
  // candidates, so it is served from that cache.
  preloadImages?: boolean
  // Lay the thumbnail strip out below the media box instead of floating it
  // over the bottom edge. The container has to leave room for it: see
  // --pdp-gallery-media-max-height in ProductDashboardGalleryModal.
  thumbnailsBelowMedia?: boolean
}

// Only the ratio is needed, so probe the smallest thumbnail candidate, which
// the strip fetches anyway on 1x displays.
const ASPECT_RATIO_PROBE_WIDTH = '52'

export const Gallery = ({
  slides,
  selectedIndex,
  onSelect,
  onImageClick,
  showNavButtons = true,
  zoomable = false,
  imagesOnly = false,
  imageSrcSetWidths = DEFAULT_SRCSET_WIDTHS,
  imageSizes = DEFAULT_SIZES,
  preloadImages = false,
  thumbnailsBelowMedia = false,
}: GalleryProps) => {
  const { siteCode } = usePage()
  // A zoomed image pans with the finger instead, so swiping is held off until
  // it is zoomed back out.
  const [isImageZoomed, setIsImageZoomed] = useState(false)

  // Every slide renders in a box shaped like the first image, so the gallery
  // keeps the same dimensions whichever image or video is selected.
  const firstImageSlide = slides.find((slide) => {
    return slide.type === 'image' && !!slide.src
  })
  const mediaAspectRatio = useImageAspectRatio(
    firstImageSlide
      ? getImageSrc(
          convertProductImageUrl(firstImageSlide.src, siteCode),
          ASPECT_RATIO_PROBE_WIDTH
        ) ?? undefined
      : undefined
  )

  usePreloadSlideImages({
    slides,
    selectedIndex,
    srcSetWidths: imageSrcSetWidths,
    sizes: imageSizes,
    enabled: preloadImages,
  })

  // Paging walks the same list the thumbnail strip shows, so with imagesOnly
  // it steps over the video. Falls back to the first or last of them if the
  // selected slide is not in the list.
  const navigableSlides = getVisibleThumbnailSlides(slides, imagesOnly)
  const navigablePosition = Math.max(
    0,
    navigableSlides.findIndex(({ index }) => {
      return index === selectedIndex
    })
  )

  const goToPrev = () => {
    const count = navigableSlides.length
    const prev = navigableSlides[(navigablePosition - 1 + count) % count]

    if (prev) {
      onSelect(prev.index)
    }
  }

  const goToNext = () => {
    const count = navigableSlides.length
    const next = navigableSlides[(navigablePosition + 1) % count]

    if (next) {
      onSelect(next.index)
    }
  }

  // Below 1024px a horizontal swipe over the media pages the slides. Swiping
  // left moves forward, matching the direction the content moves.
  const swipeHandlers = useSwipeNavigation({
    enabled: navigableSlides.length > 1 && !isImageZoomed,
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrev,
  })

  if (slides.length === 0) {
    return null
  }

  const activeSlide = slides[selectedIndex] ?? slides[0]
  const showThumbnails = hasVisibleThumbnails(slides, imagesOnly)

  return (
    <div
      className={classNames(styles.root, {
        [styles.thumbnailsBelowMedia]: thumbnailsBelowMedia,
      })}>
      <div
        className={classNames(styles.media, {
          [styles.mediaVideo]: activeSlide.type === 'video',
          [styles.mediaNoThumbnails]: !showThumbnails,
        })}
        style={
          {
            '--pdp-gallery-media-aspect-ratio': mediaAspectRatio,
          } as CSSProperties
        }
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchEnd={swipeHandlers.onTouchEnd}
        onTouchCancel={swipeHandlers.onTouchCancel}>
        {activeSlide.type === 'image' && (
          <GalleryImage
            slide={activeSlide}
            priority={selectedIndex === 0}
            onClick={onImageClick}
            zoomable={zoomable}
            srcSetWidths={imageSrcSetWidths}
            sizes={imageSizes}
            onZoomChange={setIsImageZoomed}
          />
        )}

        {activeSlide.type === 'video' && (
          <div className={styles.videoWrapper}>
            <ProductVideo
              productSlideVideo={activeSlide.src}
              className={styles.video}
            />
          </div>
        )}

        {showNavButtons && navigableSlides.length > 1 && (
          <>
            <button
              type='button'
              aria-label='Previous image'
              onClick={goToPrev}
              className={classNames(styles.navButton, styles.prev)}>
              <IconChevronLeft size={18} strokeWidth={3} />
            </button>
            <button
              type='button'
              aria-label='Next image'
              onClick={goToNext}
              className={classNames(styles.navButton, styles.next)}>
              <IconChevronRight size={18} strokeWidth={3} />
            </button>
          </>
        )}
      </div>

      <GalleryThumbnails
        slides={slides}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        imagesOnly={imagesOnly}
        belowMedia={thumbnailsBelowMedia}
      />
    </div>
  )
}
